<template>
    <!--
      SSH 文件面板（内嵌在控制台抽屉右侧，不是独立抽屉）
      入口在控制台的「文件」按钮：开完控制台后想传/下个文件，就地展开即可，终端始终可见。
    -->
    <div class="fp">
        <!-- 面板头 -->
        <div class="fp-head">
            <span class="fp-head__title">文件</span>
            <span class="fp-head__count">{{ items.length }} 项</span>
            <div class="fp-head__ops">
                <el-tooltip content="刷新" placement="top">
                    <el-button class="fp-icon-btn" size="small" text :disabled="loading || uploading"
                        @click="refresh">
                        <el-icon>
                            <Refresh />
                        </el-icon>
                    </el-button>
                </el-tooltip>
                <el-tooltip content="上传到当前目录" placement="top">
                    <el-button class="fp-icon-btn" size="small" text type="primary" :loading="uploading"
                        :disabled="loading" @click="pickFile">
                        <el-icon v-if="!uploading">
                            <Upload />
                        </el-icon>
                    </el-button>
                </el-tooltip>
                <el-tooltip content="收起文件面板" placement="top">
                    <el-button class="fp-icon-btn" size="small" text @click="$emit('close')">
                        <el-icon>
                            <Close />
                        </el-icon>
                    </el-button>
                </el-tooltip>
            </div>
        </div>

        <!-- 路径栏 -->
        <div class="fp-path">
            <el-tooltip content="上级目录" placement="top">
                <el-button class="fp-path__btn" size="small" text :disabled="!canGoUp || loading" @click="goUp">
                    <el-icon>
                        <ArrowLeft />
                    </el-icon>
                </el-button>
            </el-tooltip>
            <!-- 面包屑：点任意一层可直接跳过去，不用逐层点「上级」 -->
            <div class="fp-crumb" :title="path">
                <span class="fp-crumb__item" :class="{ 'is-current': !breadcrumbs.length }" @click="goTo('/')">
                    /
                </span>
                <template v-for="(item, index) in breadcrumbs" :key="item.path">
                    <span v-if="index > 0" class="fp-crumb__sep">/</span>
                    <span class="fp-crumb__item" :class="{ 'is-current': item.current }"
                        @click="goTo(item.path)">{{ item.name }}</span>
                </template>
            </div>
            <el-tooltip content="复制当前路径" placement="top">
                <el-button class="fp-path__btn" size="small" text :disabled="!path" @click="copyPath">
                    <el-icon>
                        <DocumentCopy />
                    </el-icon>
                </el-button>
            </el-tooltip>
            <el-tooltip :content="showHidden ? '不显示隐藏文件' : '显示隐藏文件'" placement="top">
                <el-button class="fp-path__btn" :class="{ 'is-on': showHidden }" size="small" text
                    @click="showHidden = !showHidden">
                    <el-icon>
                        <View />
                    </el-icon>
                </el-button>
            </el-tooltip>
        </div>

        <!-- 文件列表：窄面板用两行式列表，比表格更省横向空间 -->
        <div v-loading="loading" class="fp-list">
            <div v-for="item in visibleItems" :key="item.path" class="fp-row" @dblclick="onRowDblclick(item)">
                <el-icon class="fp-row__icon" :class="{ 'is-dir': item.dir }">
                    <Folder v-if="item.dir" />
                    <Link v-else-if="item.link" />
                    <Document v-else />
                </el-icon>
                <div class="fp-row__main">
                    <div class="fp-row__name" :class="{ 'is-dir': item.dir }" :title="item.name"
                        @click="onNameClick(item)">
                        {{ item.name }}
                    </div>
                    <div class="fp-row__meta">
                        <span :title="item.dir ? '目录' : item.sizeText">{{ item.dir ? '-' : item.sizeText }}</span>
                        <span class="fp-row__sep">·</span>
                        <span>{{ item.modifyTime }}</span>
                        <span class="fp-row__perm" :title="item.permission">{{ item.permission }}</span>
                    </div>
                </div>
                <div v-if="!item.dir" class="fp-row__ops">
                    <el-tooltip v-if="canPreview(item)" content="在线预览" placement="top">
                        <el-button class="fp-icon-btn" size="small" text type="primary"
                            @click.stop="openPreview(item)">
                            <el-icon>
                                <View />
                            </el-icon>
                        </el-button>
                    </el-tooltip>
                    <el-tooltip content="下载" placement="top">
                        <el-button class="fp-icon-btn" size="small" text type="primary" :disabled="downloading"
                            @click.stop="download(item)">
                            <el-icon>
                                <Download />
                            </el-icon>
                        </el-button>
                    </el-tooltip>
                </div>
            </div>
            <div v-if="!loading && visibleItems.length === 0" class="fp-empty">
                {{ items.length === 0 ? '这个目录是空的' : '隐藏了 ' + (items.length - visibleItems.length) + ' 个隐藏文件' }}
            </div>
        </div>

        <!-- 隐藏的上传输入框（用原生 input，避免额外引入组件） -->
        <input ref="fileInput" class="fp-input" type="file" @change="onFilePicked" />
    </div>

    <!-- 文本预览：不方便下载的场景可以直接看内容 -->
    <el-dialog v-model="preview.visible" class="ssh-file-preview" width="80%" top="6vh" append-to-body
        :title="preview.name || '文件预览'">
        <div class="ssh-file-preview__bar">
            <span class="ssh-file-preview__path" :title="preview.path">{{ preview.path }}</span>
            <span class="ssh-file-preview__size">{{ preview.sizeText }}</span>
            <el-button class="ssh-file-preview__btn" size="small" type="primary" plain
                :disabled="preview.loading || !preview.rawContent" @click="toggleJsonFormat">
                {{ preview.formatted ? '还原原文' : '格式化 JSON' }}
            </el-button>
        </div>
        <!-- 文件属性：修改时间 + 权限（含人类语言说明），一眼看懂"这个文件是什么、谁能动它" -->
        <div class="ssh-file-preview__meta">
            <span class="ssh-file-preview__meta-item">最后修改：{{ preview.modifyTime || '-' }}</span>
            <span class="ssh-file-preview__meta-sep">·</span>
            <span class="ssh-file-preview__meta-item">
                权限：<span class="ssh-file-preview__perm">{{ preview.permission || '-' }}</span>
                <span v-if="permissionOctal" class="ssh-file-preview__octal">（{{ permissionOctal }}）</span>
            </span>
            <template v-if="permissionDescription">
                <span class="ssh-file-preview__meta-sep">·</span>
                <span class="ssh-file-preview__meta-item is-desc">{{ permissionDescription }}</span>
            </template>
        </div>
        <div v-loading="preview.loading" class="ssh-file-preview__body">
            <div class="ssh-file-preview__inner">
                <pre class="ssh-file-preview__gutter">{{ previewLineNumbers }}</pre>
                <pre class="ssh-file-preview__code">{{ previewText }}</pre>
            </div>
        </div>
    </el-dialog>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus';
import request from '@/utils/request.js';
import { config } from '@/utils/config';

export default {
    name: 'SshFilePanel',
    props: {
        machine: { type: Object, default: () => ({}) }
    },
    emits: ['close'],
    data() {
        return {
            // 当前目录（由后端返回的规范化绝对路径）
            path: '',
            items: [],
            loading: false,
            // 同一时刻只允许一个下载/上传，避免误触
            downloading: false,
            uploading: false,
            showHidden: false,
            // 预览大小上限（由后端 list 接口下发，取值见 SshSftpService.PREVIEW_MAX_BYTES）
            previewMaxBytes: 0,
            preview: {
                visible: false,
                loading: false,
                name: '',
                path: '',
                sizeText: '',
                // 修改时间与权限来自列表行（预览接口只返回内容，不必为此再查一次文件属性）
                modifyTime: '',
                permission: '',
                content: '',
                // 原文永久保留：JSON 格式化以它为源，反复切换不会层层缩进
                rawContent: '',
                formatted: false
            }
        };
    },
    computed: {
        visibleItems() {
            // 后端会返回点开头的隐藏文件，这里做一个显示开关
            return this.showHidden ? this.items : this.items.filter(item => !item.name.startsWith('.'));
        },
        canGoUp() {
            return !!this.path && this.path !== '/';
        },
        /** 权限的八进制写法（如 644 / 4755），方便直接对着敲 chmod */
        permissionOctal() {
            const perm = this.preview.permission;
            if (!perm || perm.length < 10) {
                return '';
            }
            const toDigit = bits => (bits[0] === 'r' ? 4 : 0) + (bits[1] === 'w' ? 2 : 0)
                + ('xst'.includes(bits[2]) ? 1 : 0);
            const digits = String(toDigit(perm.slice(1, 4))) + String(toDigit(perm.slice(4, 7)))
                + String(toDigit(perm.slice(7, 10)));
            // setuid / setgid / sticky 是第四位（只在存在时才显示，避免把 4755 说成 755）
            const special = (perm[3] === 's' || perm[3] === 'S' ? 4 : 0)
                + (perm[6] === 's' || perm[6] === 'S' ? 2 : 0)
                + (perm[9] === 't' || perm[9] === 'T' ? 1 : 0);
            return special > 0 ? String(special) + digits : digits;
        },
        /** 把 -rw-r--r-- 这类权限串翻译成人话 */
        permissionDescription() {
            const perm = this.preview.permission;
            if (!perm || perm.length < 10) {
                return '';
            }
            const typeMap = {
                '-': '普通文件', d: '目录', l: '软链接',
                c: '字符设备', b: '块设备', p: '命名管道', s: '套接字'
            };
            const isDir = perm[0] === 'd';
            // 目录与普通文件的 r/w/x 语义不同，分开说才准确
            const readText = isDir ? '列目录' : '读';
            const writeText = isDir ? '增删' : '写';
            const execText = isDir ? '进入' : '执行';
            const describe = bits => {
                const parts = [];
                if (bits[0] === 'r') {
                    parts.push(readText);
                }
                if (bits[1] === 'w') {
                    parts.push(writeText);
                }
                // 执行位可能是 s / S / t / T（带特殊位时的大小写形式）
                if ('xst'.includes(bits[2])) {
                    parts.push(execText);
                }
                return parts.length ? parts.join('、') : '无权限';
            };
            return (typeMap[perm[0]] || '未知类型')
                + ' ｜ 属主：' + describe(perm.slice(1, 4))
                + ' ｜ 同组：' + describe(perm.slice(4, 7))
                + ' ｜ 其他人：' + describe(perm.slice(7, 10));
        },
        /**
         * 路径面包屑：把 /home/user/www 拆成 [{home,/home}, {user,/home/user}, {www,/home/user/www}]
         * 根目录 `/` 单独渲染（它就是开头的那个斜杠），所以这里不包含根
         */
        breadcrumbs() {
            if (!this.path || this.path === '/') {
                return [];
            }
            const names = this.path.replace(/^\//, '').split('/');
            return names.map((name, index) => ({
                name,
                path: '/' + names.slice(0, index + 1).join('/'),
                current: index === names.length - 1
            }));
        },
        /**
         * 去掉结尾的一个换行：
         * `pre` 对"结尾空行"的渲染在各浏览器上不一致，而这里行号与内容必须严格 1:1，
         * 所以两者都基于这个字符串计算，避免底部多出一个行号
         */
        previewText() {
            const content = this.preview.content || '';
            return content.endsWith('\n') ? content.slice(0, -1) : content;
        },
        /**
         * 行号列的内容。
         * 【为什么用整块文本而不是逐行 v-for】逐行渲染会产生几万个 DOM 节点，大文件直接把页面卡死；
         * 这里整列只是一个文本节点，靠相同的 font-size / line-height 与内容严格对齐。
         */
        previewLineNumbers() {
            const text = this.previewText;
            if (!text) {
                return '';
            }
            const count = text.split('\n').length;
            const lines = new Array(count);
            for (let i = 0; i < count; i++) {
                lines[i] = i + 1;
            }
            return lines.join('\n');
        }
    },
    mounted() {
        // 面板是「展开时才创建」的，所以直接在 mounted 里加载，不需要 watch 显隐
        this.load('');
    },
    methods: {
        // ---------- 浏览 ----------
        refresh() {
            this.load(this.path);
        },
        goUp() {
            if (!this.canGoUp) {
                return;
            }
            const index = this.path.lastIndexOf('/');
            this.load(index <= 0 ? '/' : this.path.substring(0, index));
        },
        /** 点面包屑的某一层：直接跳到那一层（点当前层不重复请求） */
        goTo(path) {
            if (!path || path === this.path || this.loading) {
                return;
            }
            this.load(path);
        },
        load(path) {
            if (!this.machine || !this.machine.id) {
                return;
            }
            this.loading = true;
            // path 传空字符串时后端会打开登录用户的 home 目录
            request({
                url: '/serverManFile/list',
                method: 'post',
                data: { machineId: this.machine.id, path: path || '' }
            })
                .then(res => {
                    this.path = res.meta.path;
                    this.items = res.meta.items || [];
                    this.previewMaxBytes = res.meta.previewMaxBytes || 0;
                })
                .catch(() => {
                    // 错误提示由 request 拦截器统一处理
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        onNameClick(item) {
            if (item.dir) {
                this.load(item.path);
            }
        },
        onRowDblclick(item) {
            if (item.dir) {
                this.load(item.path);
            }
        },

        /** 复制当前完整路径 */
        copyPath() {
            const text = this.path;
            if (!text) {
                return;
            }
            // 优先用 Clipboard API：它只在**安全上下文**（https 或 localhost）下可用
            if (navigator.clipboard && window.isSecureContext) {
                navigator.clipboard.writeText(text)
                    .then(() => ElMessage.success('已复制路径'))
                    .catch(() => this.copyPathFallback(text));
                return;
            }
            this.copyPathFallback(text);
        },
        /**
         * 兜底复制：用临时 textarea + execCommand。
         * 场景：通过 http + 非 localhost 访问时（比如 http://192.168.1.5:8001），
         * Clipboard API 整个不可用，只能用这个已废弃但仍被浏览器支持的老办法。
         */
        copyPathFallback(text) {
            const textarea = document.createElement('textarea');
            textarea.value = text;
            // 固定在视口外并透明，避免复制时页面跳动
            textarea.style.position = 'fixed';
            textarea.style.top = '-1000px';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            let ok = false;
            try {
                ok = document.execCommand('copy');
            } catch (e) {
                ok = false;
            }
            document.body.removeChild(textarea);
            if (ok) {
                ElMessage.success('已复制路径');
            } else {
                ElMessage({ message: '复制失败，请手动选中路径复制', type: 'warning', duration: 3 * 1000 });
            }
        },

        // ---------- 下载 ----------
        download(item) {
            if (this.downloading) {
                return;
            }
            this.downloading = true;
            // 浏览器原生下载无法自定义请求头，所以先用正常鉴权换一张一次性票
            request({
                url: '/serverManFile/ticket',
                method: 'post',
                data: { machineId: this.machine.id, path: item.path }
            })
                .then(res => {
                    this.openDownloadUrl(res.meta.ticket);
                })
                .catch(() => {})
                .finally(() => {
                    this.downloading = false;
                });
        },
        openDownloadUrl(ticket) {
            // 后端返回的是 Content-Disposition: attachment，浏览器会在新标签里开始下载并自动关掉它；
            // 用新标签而不是当前页跳转，是为了让票失效等错误不影响当前页面
            const baseServer = (config() && config().baseServer) || '';
            const url = baseServer.replace(/\/+$/, '') + '/openapi/serverManFile/download?ticket='
                + encodeURIComponent(ticket);
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            link.rel = 'noopener';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },

        // ---------- 预览 ----------
        canPreview(item) {
            // 后端还会再校验一次，这里只是避免给用户一个必然失败的按钮
            return !item.dir && (!this.previewMaxBytes || item.size <= this.previewMaxBytes);
        },
        openPreview(item) {
            this.preview.visible = true;
            this.preview.loading = true;
            this.preview.name = item.name;
            this.preview.path = item.path;
            this.preview.sizeText = item.sizeText;
            this.preview.modifyTime = item.modifyTime;
            this.preview.permission = item.permission;
            this.preview.content = '';
            this.preview.rawContent = '';
            this.preview.formatted = false;

            request({
                url: '/serverManFile/preview',
                method: 'post',
                data: { machineId: this.machine.id, path: item.path }
            })
                .then(res => {
                    this.preview.name = res.meta.name;
                    this.preview.path = res.meta.path;
                    this.preview.rawContent = res.meta.content || '';
                    this.preview.content = this.preview.rawContent;
                })
                .catch(() => {
                    // 失败原因（过大 / 二进制 / 无权限）已由拦截器提示，这里直接关掉弹窗
                    this.preview.visible = false;
                })
                .finally(() => {
                    this.preview.loading = false;
                });
        },
        toggleJsonFormat() {
            if (this.preview.formatted) {
                this.preview.content = this.preview.rawContent;
                this.preview.formatted = false;
                return;
            }
            try {
                // 始终以原文为源做格式化，来回切换不会层层缩进
                const parsed = JSON.parse(this.preview.rawContent);
                this.preview.content = JSON.stringify(parsed, null, 4);
                this.preview.formatted = true;
            } catch (e) {
                ElMessage({ message: '内容不是合法的 JSON，无法格式化', type: 'warning', duration: 3 * 1000 });
            }
        },

        // ---------- 上传 ----------
        pickFile() {
            // 先清空，否则连续选同一个文件不会触发 change
            this.$refs.fileInput.value = '';
            this.$refs.fileInput.click();
        },
        onFilePicked(event) {
            const file = event.target.files && event.target.files[0];
            if (!file) {
                return;
            }
            // 当前目录已有同名文件时先确认一次；后端也会挡，这里只是给出更友好的交互
            const conflict = this.items.some(item => item.name === file.name);
            if (conflict) {
                ElMessageBox.confirm(`当前目录已存在同名文件「${file.name}」，确定要覆盖吗？`, '覆盖确认', {
                    confirmButtonText: '覆盖',
                    cancelButtonText: '取消',
                    type: 'warning'
                })
                    .then(() => this.upload(file, true))
                    .catch(() => {});
            } else {
                this.upload(file, false);
            }
        },
        upload(file, overwrite) {
            const form = new FormData();
            form.append('machineId', this.machine.id);
            form.append('path', this.path || '');
            form.append('overwrite', overwrite ? 'true' : 'false');
            form.append('file', file);

            this.uploading = true;
            request({
                url: '/serverManFile/upload',
                method: 'post',
                data: form,
                // 大文件上传可能远超默认的 120 秒，这里显式关闭超时
                timeout: 0
            })
                .then(res => {
                    ElMessage.success('上传完成：' + res.meta.path);
                    this.refresh();
                })
                .catch(() => {})
                .finally(() => {
                    this.uploading = false;
                });
        }
    }
};
</script>

<style lang="scss" scoped>
/* 面板本体：内嵌在控制台抽屉右侧，所以左边给一条分隔线、自己撑满高度 */
.fp {
    display: flex;
    flex: none;
    flex-direction: column;
    width: 380px;
    height: 100%;
    border-left: 1px solid #e6eaf0;
    background-color: #ffffff;
}

/* ---------- 面板头 ---------- */
.fp-head {
    display: flex;
    flex: none;
    align-items: center;
    gap: 8px;
    padding: 8px 10px;
    border-bottom: 1px solid #eef1f6;
    background: linear-gradient(180deg, #fbfcfe 0%, #ffffff 100%);
}

.fp-head__title {
    font-size: 13px;
    font-weight: 600;
    color: #1f2d3d;
}

.fp-head__count {
    flex: 1 1 auto;
    font-size: 12px;
    color: #aab4c4;
}

.fp-head__ops {
    display: flex;
    flex: none;
    align-items: center;
    gap: 2px;
}

.fp-icon-btn {
    height: 24px;
    margin-left: 0;
    padding: 0 6px;
}

/* ---------- 路径栏 ---------- */
.fp-path {
    display: flex;
    flex: none;
    align-items: center;
    gap: 2px;
    padding: 6px 6px 6px 8px;
    border-bottom: 1px solid #f2f5fa;
    background-color: #fbfcfe;
}

.fp-path__btn {
    flex: none;
    height: 22px;
    margin-left: 0;
    padding: 0 5px;
    color: #8a94a6;
}

.fp-path__btn:hover {
    color: var(--el-color-primary);
    background-color: transparent;
}

/* 隐藏文件开关打开时高亮，避免"看不出当前是开还是关" */
.fp-path__btn.is-on {
    color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);
}

/* 面包屑：点任意一层直接跳过去；路径过长时折行 */
.fp-crumb {
    display: flex;
    flex: 1 1 auto;
    flex-wrap: wrap;
    align-items: center;
    min-width: 0;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
    line-height: 18px;
}

.fp-crumb__item {
    max-width: 100%;
    padding: 0 3px;
    border-radius: 4px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--el-color-primary);
    cursor: pointer;
}

.fp-crumb__item:hover {
    background-color: var(--el-color-primary-light-9);
}

/* 当前所在层：不可点，用正文色，避免"点了没反应"的困惑 */
.fp-crumb__item.is-current {
    color: #1f2d3d;
    cursor: default;
}

.fp-crumb__item.is-current:hover {
    background-color: transparent;
}

.fp-crumb__sep {
    color: #c0c4cc;
    user-select: none;
}

/* ---------- 列表 ---------- */
.fp-list {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    padding: 4px 0;
}

.fp-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 5px 8px 5px 10px;
    transition: background-color 0.15s;
}

.fp-row:hover {
    background-color: #f6f9ff;
}

.fp-row__icon {
    flex: none;
    color: #a3b1c6;
}

.fp-row__icon.is-dir {
    color: var(--el-color-primary-light-3);
}

.fp-row__main {
    flex: 1 1 auto;
    min-width: 0;
}

.fp-row__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 13px;
    color: #4a5568;
}

.fp-row__name.is-dir {
    color: var(--el-color-primary);
    cursor: pointer;
}

.fp-row__name.is-dir:hover {
    text-decoration: underline;
}

.fp-row__meta {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: 1px;
    overflow: hidden;
    white-space: nowrap;
    font-size: 11px;
    color: #aab4c4;
}

.fp-row__sep {
    color: #d8dee8;
}

.fp-row__perm {
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}

.fp-row__ops {
    display: flex;
    flex: none;
    align-items: center;
    gap: 2px;
}

.fp-empty {
    padding: 24px 12px;
    text-align: center;
    font-size: 12px;
    color: #aab4c4;
}

.fp-input {
    display: none;
}

/* ---------- 预览弹窗 ---------- */
.ssh-file-preview__bar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding-bottom: 10px;
}

.ssh-file-preview__path {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
    color: #8a94a6;
}

.ssh-file-preview__size {
    flex: none;
    font-size: 12px;
    color: #8a94a6;
}

.ssh-file-preview__btn {
    flex: none;
    margin-left: 0;
    border-radius: 8px;
}

/* 文件属性行：修改时间 + 权限（含人话说明）；内容长时自动折行 */
.ssh-file-preview__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-bottom: 10px;
    padding: 6px 10px;
    border-radius: 8px;
    background-color: #f6f8fb;
    font-size: 12px;
    line-height: 18px;
    color: #5a6472;
}

.ssh-file-preview__meta-item {
    min-width: 0;
}

.ssh-file-preview__meta-sep {
    color: #c8cdd6;
    user-select: none;
}

.ssh-file-preview__perm {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    color: #1f2d3d;
}

/* 八进制写法用暖色，方便一眼找到（要 chmod 时直接抄） */
.ssh-file-preview__octal {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    color: #e6a23c;
}

/* 权限的人话说明：比主信息弱一点，不抢焦点 */
.ssh-file-preview__meta-item.is-desc {
    color: #8a94a6;
}

.ssh-file-preview__body {
    max-height: 64vh;
    min-height: 120px;
    overflow: auto;
    border: 1px solid #eef1f6;
    border-radius: 10px;
    background-color: #fbfcfe;
}

.ssh-file-preview__inner {
    display: flex;
    align-items: flex-start;
    width: max-content;
    min-width: 100%;
}

/* 行号列：sticky 保证横向滚动时也一直可见 */
.ssh-file-preview__gutter {
    position: sticky;
    left: 0;
    z-index: 1;
    flex: none;
    margin: 0;
    padding: 12px 10px 12px 14px;
    border-right: 1px solid #eef1f6;
    background-color: #f6f8fb;
    text-align: right;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12.5px;
    line-height: 1.6;
    color: #a3b1c6;
    user-select: none;
}

/* 内容列：不换行，才能与行号严格 1:1 对齐（长行靠横向滚动查看） */
.ssh-file-preview__code {
    flex: none;
    margin: 0;
    padding: 12px 14px;
    white-space: pre;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12.5px;
    line-height: 1.6;
    color: #1f2d3d;
    tab-size: 4;
}

/* ---------- 小屏适配 ---------- */
@media (max-width: 768px) {
    /* 手机上终端和文件面板无法并排：面板铺满，收起后回到终端 */
    .fp {
        width: 100%;
        border-left: 0;
    }
}
</style>

<!-- 预览弹窗：append-to-body 之后拿不到 scoped 属性，Element 内部结构的样式要放在非 scoped 块里 -->
<style lang="scss">
.ssh-file-preview .el-dialog__body {
    padding: 0 16px 16px;
}
</style>
